/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : File is used to list documents uploaded by users. 
 *   Integrations : NA
 *   Version : 1.0.0
*/

// custom packages
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// custom components
import { CustomSearchInput } from '../../Components/FormElements';
import { CustomDataTable } from '../../Components/DataTable';
import { APIService } from '../../Service';
import { Chip, EmptyScreen } from '../../Components/Others';

const DocumentList = () => {

    const navigate = useNavigate();
    const isRendered = useRef(true);
    const scrollView = useRef(null)
    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [documentData, setDocumentData] = useState({});
    const [onPaginationChange, setOnPaginationChange] = useState(false);
    const [searched, setSearched] = useState(false);
    const [loader, setLoader] = useState(true);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);



    useEffect(() => {
        if (isRendered.current === true) {
            _handleGetDocumentList();
        }
        isRendered.current = false;
    }, []);

    // handle table page change 
    const _handleTablePageChange = (page_num) => {

        setCurrentPage(page_num);
        _handleGetDocumentList(page_num);
        if (scrollView.current) {
            scrollView.current.scrollIntoView({ behavior: 'smooth', display: 'start' })
        }
    };

    // handle search clear
    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        if (searched === true) {
            _handleGetDocumentList(currentPage, "");
        }
    };

    // on search click
    const _onSearch = () => {
        setSearched(true);
        setLoader(true);
        _handleGetDocumentList(1);
    };

    // handle navigate to detail
    const _handleNavigate = (id) => {
        navigate(`${id}`);
    };

    const TABLE_COLUMN = [
        {
            name: 'Customer ID',
            selector: row => row.rayi_customer_id,
            width: "140px",
            sortable: true
        },
        {
            name: 'Name',
            selector: row => row.name,
            cell: (row) =>
                <span className={`${row.name ? "text-decoration-hover-underline e-text-blue-violet" : "e-text-smoky-black"} text-truncate e-text-blue-violet `}
                    onClick={() => _handleNavigate(row.customer_id)}>
                    {row.name ? row.name : '-'}
                </span>,
            width: "280px",
            sortable: true
        },
        {
            name: 'Joined on',
            selector: row => row.joined_on,
            width: "160px",
            sortable: true,
            selector: row => new Date(row.joined_on),
            format: row => row.joined_on,
        },
        {
            name: 'Email',
            cell: (row) =>
                <a href={`mailto:${row.email}`} target='_blank' className=' text-truncate e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.email}
                </a>,
            width: "280px"
        },
        {
            name: 'Phone number',
            cell: (row) =>
                <a href={`tel:${row.phone}`} className='e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.phone}
                </a>,
            width: "200px"
        },
        {
            name: 'Status',
            cell: (row) =>
                <Chip label={row.is_data_deleted === true ? "Data deleted" : "Active"}
                    tooltip={row.is_data_deleted === true ? true : false}
                    content={row.is_data_deleted === true ? "There are no documents" : ""}
                    id={row.customer_id}
                    className={
                        row.is_data_deleted === false ? "e-bg-green-crayola" :
                            row.is_data_deleted === true ? "e-bg-maximum-red " :
                                "e-bg-bleu-de-france"
                    } />,
            width: "200px"
        },
    ];


    // API -Document list
    const _handleGetDocumentList = (page_num = currentPage, search = searchValue) => {
        let is_post = true;
        let url = 'document/list';
        let request = {
            "query": search,
            "page_num": page_num,
            "page_size": 10
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setDocumentData(response.data);
            } else {
                setDocumentData({ data: [] });
            }
            setLoader(false);
            setOnPaginationChange(false);
        })
    };



    return (
        <section className='e-section-height pb-5' >
            <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                    Documents & Actions
                </h3>
            </div>

            <div className='padding-52px-lr margin--80px-top '>
                <div ref={scrollView} className='e-bg-white border-radius-16 min-vh-50   padding-32px-tb '>
                    <div className='padding-32px-lr d-flex justify-content-between  align-items-center'>
                        <h4 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                            Customers list
                        </h4>
                        {/* Search input */}
                        <CustomSearchInput placeholder="Search with customer ID or name"
                            type="text"
                            onSearch={() => _onSearch()}
                            onSearchClear={_handleSearchClear}
                            value={searchValue}
                            onKeyPress={() => _onSearch()}
                            onClear={_handleSearchClear}
                            className="w-340px"
                            handleChange={(e) => {
                                setSearchValue(e);
                                if (e.length === 0) {
                                    _handleSearchClear()
                                }
                            }} />
                    </div>

                    {/* Customers list table */}
                    {loader ?
                        <div className='d-flex align-items-center justify-content-center min-vh-50 '>
                            <img src={require("../../Assets/Image/loader.gif")}
                                draggable={false}
                                width={80}
                                height={80}
                                alt='loader'
                                className='text-center' />
                        </div>
                        :
                        documentData.data.length > 0 ?
                            <div className='e-data-table-wrapper-updated pt-4 padding-32px-'>
                                <CustomDataTable columns={TABLE_COLUMN}
                                    data={documentData.data}
                                    pagination={true}
                                    lastPage={documentData.total_page}
                                    paginationPerPage={10}
                                    paginationDefaultPage={documentData.current_page}
                                    paginationTotalRows={documentData.total_count}
                                    onChangePage={_handleTablePageChange}
                                    rowClick={(row) => row.is_data_deleted === true ? null : _handleNavigate(row.customer_id)} />
                            </div>
                            :
                            <EmptyScreen
                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                title='No results found'
                                description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                            />
                    }

                </div>
            </div>
        </section>
    )
}

export default DocumentList;
